/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';

import template from './targeting-editor-modal.component.html';
import {TargetingHelper} from './targeting-helper';
import {
  TargetingEditorOptions,
  TargetingEditorResult,
  TargetingEditorState
} from './targeting-editor-state';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';

@Component({
  standalone: false,
  selector: 'adn-targeting-editor-modal',
  template,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    form {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      margin: 0;
    }

    .modal-header,
    .modal-footer {
      flex: 0 0 auto;
    }

    .modal-body {
      flex: 1 1 auto;
      overflow-y: auto;
      min-height: 0;
    }
  `]
})
export class TargetingEditorModalComponent implements OnInit, AfterViewInit, OnDestroy, TargetingEditorOptions {
  /** Set via BsModalService initialState */
  targeting: any;
  modelLineItem: any;
  pageType: string;
  reachEnabled: string;
  availableTargets: any;
  mandatoryTargets: any;
  overridableTargeting: any;
  targetingMode: string;
  lineItemType: string;
  isLockedDown: boolean;

  readonly onSaved = new Subject<TargetingEditorResult>();

  state: TargetingEditorState;
  saving = false;

  private initialTargetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public readonly bsModalRef: BsModalRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.document.addEventListener('keydown', this.handleModalEscape, true);
    this.state = new TargetingEditorState({
      targeting: this.targeting,
      modelLineItem: this.modelLineItem,
      pageType: this.pageType,
      reachEnabled: this.reachEnabled,
      availableTargets: this.availableTargets,
      mandatoryTargets: this.mandatoryTargets,
      overridableTargeting: this.overridableTargeting,
      targetingMode: this.targetingMode,
      lineItemType: this.lineItemType,
      isLockedDown: this.isLockedDown
    }, {
      LocalNetworkProfile: this.LocalNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      localUserProfile: this.localUserProfile
    });
    this.state.tabController.onAction = () => {
      this.state.calcSizes();
      safeDetectChanges(this.cdr);
    };
  }

  ngAfterViewInit(): void {
    const initialTarget = this.state?.targetEntries?.[0]?.target;
    if (!initialTarget?.widget) {
      return;
    }
    this.initialTargetTimer = setTimeout(() => {
      this.initialTargetTimer = null;
      this.state.tabController.onTabSelect(initialTarget.widget, false);
    });
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('keydown', this.handleModalEscape, true);
    if (this.initialTargetTimer) {
      clearTimeout(this.initialTargetTimer);
      this.initialTargetTimer = null;
    }
  }

  onTargetSelected(entry: {key: string; target: any}): void {
    this.state.onTargetTabSelect(entry.target);
  }

  handleModalKeypress(event: KeyboardEvent): void {
    if (TargetingHelper.shouldSuppressModalEnter(event)) {
      TargetingHelper.stopEvent(event);
    }
  }

  saveAndClose(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.state.tabController.tabBeforeSubmit().then(() => {
      this.onSaved.next({
        wc: this.state.underModel || this.state.targeting,
        overrides: this.state.overridableTargeting,
        reset: this.state.resetObj.reset
      });
      this.bsModalRef.hide();
    }, () => {
      this.saving = false;
      safeDetectChanges(this.cdr);
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  readonly handleModalEscape = (event: KeyboardEvent): void => {
    if ((event.key !== 'Escape' && event.keyCode !== 27) || !this.isTopModal()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dismiss();
  };

  private isTopModal(): boolean {
    const modalContainers = this.document.querySelectorAll('modal-container');
    const topModal = modalContainers.item(modalContainers.length - 1);
    return !!topModal && topModal.contains(this.elementRef.nativeElement);
  }
}
